import { useNavigate } from "react-router-dom";
import { Fragment, SyntheticEvent, useState } from "react";

import {
  Chip,
  Container,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";

import conf from "../../conf";
import Refresh from "../utils/Refresh";
import EmptyTableRow from "../utils/Empty";
import SectionHeader from "../utils/SectionHeader";
import usePagination from "../../hooks/pagination";
import ProtectedRoute from "../auth/ProtectedRoute";
import LinearIndeterminate from "../utils/LinearLoader";
import { a11yProps, CustomTabPanel } from "./Rows";
import {
  RequestSchema,
  useProxyRequests,
  useProxyRequestDetail,
} from "../../hooks/proxy/requests";

const ClosedRow = (props: { request: RequestSchema }) => {
  const { id, status, subject, project, approver, requester, updatedAt } =
    props.request;

  const navigate = useNavigate();
  const handleClick = () => navigate(`/requests/${id}`);

  const [openDetail, setOpenDetail] = useState(false);

  const toggle = () => setOpenDetail(!openDetail);

  const chip =
    status === "approved" ? (
      <Chip color="success" label={status} onClick={handleClick} />
    ) : (
      <Chip color="error" label={status} onClick={handleClick} />
    );

  return (
    <Fragment>
      <TableRow
        hover
        key={id}
        sx={conf.STYLE.LAST_CHILD_NO_BORDER}
        onClick={toggle}
        style={{ cursor: "pointer" }}
      >
        <TableCell>{new Date(updatedAt).toLocaleString() ?? "--"}</TableCell>

        <TableCell align="right">{chip}</TableCell>

        <TableCell align="right">{requester ?? "--"}</TableCell>

        <TableCell align="right">{approver ?? "--"}</TableCell>

        <TableCell align="right">{subject ?? "--"}</TableCell>

        <TableCell align="right">
          {" "}
          <b>{project.name ?? "--"}</b>
        </TableCell>
      </TableRow>

      {openDetail && <RequestDetail id={id} />}
    </Fragment>
  );
};

const RequestDetail = (props: { id: string }) => {
  const { id } = props;
  const detail = useProxyRequestDetail({ id });

  return (
    <TableRow key={`${id}-detail`}>
      {detail.isLoading ? (
        <TableCell colSpan={7}>
          <LinearIndeterminate />
        </TableCell>
      ) : detail.isError ? (
        <TableCell colSpan={7} align="center">
          {detail.error.response?.data.message}
        </TableCell>
      ) : (
        <TableCell colSpan={7}>
          <Box>
            <TextField
              multiline
              fullWidth
              aria-readonly
              label="Detail"
              margin="normal"
              variant="filled"
              defaultValue={detail?.data?.data.message}
              InputProps={{ readOnly: true }}
            />

            <TextField
              fullWidth
              size="small"
              label="Comment"
              margin="normal"
              defaultValue={detail?.data?.data.comment}
              InputProps={{ readOnly: true }}
            />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

const RequestsClosed = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState(1);

  const paginate = usePagination({ defaultSort: "updatedAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const closed = useProxyRequests({ status: "closed", ...body });

  const handleChange = (e: SyntheticEvent, value: number) => setValue(value);

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader
          title="Requests"
          icon={<ApprovalOutlinedIcon />}
          buttons={[
            <TablePagination
              key={"requests-closed-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={closed?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"requests-closed-refresh"}
              onClick={closed.refetch}
              isFetching={closed.isFetching}
            />,
          ]}
        />

        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              variant="fullWidth"
              onChange={handleChange}
              textColor="inherit"
              aria-label="request tabs"
              indicatorColor="secondary"
            >
              <Tab
                label="Pending"
                iconPosition="start"
                {...a11yProps(0)}
                onClick={() => navigate("/requests/pending")}
              />

              <Tab label="Closed" iconPosition="start" {...a11yProps(1)} />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={1}>
            <Paper elevation={3}>
              <TableContainer sx={{ mb: 4, maxHeight: 582 }}>
                <Table aria-label="collapsible table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {conf.COLUMNS.REQUESTS.CLOSED.map((column) => (
                        <TableCell
                          key={column.key}
                          sortDirection={order}
                          align={column.isHeader ? "left" : "right"}
                        >
                          <TableSortLabel
                            active={sort === column.key}
                            direction={sort === column.key ? order : "asc"}
                            onClick={(event: React.MouseEvent<unknown>) =>
                              handleSort(event, column.key)
                            }
                          >
                            <b>{column.label}</b>
                            {sort === column.key ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {closed.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <LinearIndeterminate />
                        </TableCell>
                      </TableRow>
                    ) : closed.data?.data.length === 0 ? (
                      <EmptyTableRow colSpan={7} type="requests" />
                    ) : (
                      closed.data?.data?.map((request: RequestSchema) => (
                        <ClosedRow key={request.id} request={request} />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </CustomTabPanel>
        </Box>
      </Container>
    </ProtectedRoute>
  );
};

export default RequestsClosed;
